import Vue from 'vue'
import metaInfo from '@/modules/mixins/metaInfo'
import mixins from '@/modules/mixins/global'
import store from '@/modules/store'
import { EventBus } from '@/modules/plugins/modules/event'
import { mapGetters } from 'vuex'
import { Meal } from '@/api'
import isEmpty from 'lodash.isempty'

import DeliveryDetails from '@/components/molecules/DeliveryDetails.vue'
import CanteenAlert from '@/components/organism/canteen/CanteenAlert.vue'
import AlertGroupOrder from '@/components/organism/restaurant/alerts/AlertGroupOrder.vue'
import RestaurantBanner from '@/components/organism/restaurant/RestaurantBanner.vue'
import CartBlock from '@/components/organism/CartBlock.vue'
import AlertDelivery from '@/components/organism/restaurant/alerts/AlertDelivery.vue'
import RestaurantDetails from '@/components/organism/restaurant/RestaurantDetails.vue'
import RestaurantMenu from '@/components/organism/restaurant/RestaurantMenu.vue'
import CategoryNavigation from '@/components/atoms/CategoryNavigation.vue'
import ImageWrapper from '@/components/atoms/ImageWrapper.vue'
import ModalDeliveryStateChange from '@/components/organism/modal/ModalDeliveryStateChange.vue'
import Spinner from '@/components/atoms/Spinner'
import RestaurantSkeleton from '@/components/atoms/RestaurantSkeleton'
import BannerLanding from '@/components/organism/restaurants/BannerLanding.vue'

const restaurant = Vue.prototype.$restaurant
const cart = Vue.prototype.$cart
const user = Vue.prototype.$user
const addresses = Vue.prototype.$addresses
const meal = Vue.observable(new Meal())
const COVER_HEIGHT = 250
const CLOUD = 'cloud'
const RESIDENTIAL = 'residential'

export default {
    mixins: [metaInfo],

    components: {
        DeliveryDetails,
        CanteenAlert,
        AlertGroupOrder,
        RestaurantBanner,
        CartBlock,
        AlertDelivery,
        RestaurantDetails,
        RestaurantMenu,
        CategoryNavigation,
        ImageWrapper,
        ModalDeliveryStateChange,
        Spinner,
        RestaurantSkeleton,
        BannerLanding,
    },

    data() {
        return {
            pageLoading: true,
            pageTitle: undefined,
            pageImage: undefined,
            pageDescription: undefined,
        }
    },

    computed: {
        meal: () => meal,
        restaurant: () => restaurant,
        cart: () => cart,
        ...mapGetters({
            time: 'order/time',
            address: 'order/address',
            allowance: 'allowance',
            smDevice: 'smDevice',
            lgDevice: 'lgDevice',
            size: 'size',
        }),
        coverHeight() {
            return this.lgDevice ? `${COVER_HEIGHT}px` : `${COVER_HEIGHT * 0.8}px`
        },
        hasAddress() {
            return !isEmpty(this.address)
        },
        activeMenu() {
            return !isEmpty(this.categories)
                ? this.$route.params.menu
                    ? this.categories?.find(
                          (category) => category.id === parseInt(this.$route.params.menu)
                      ) || this.categories[0]
                    : this.categories[0]
                : {}
        },
        loading() {
            return this.restaurant && this.restaurant.id ? this.restaurant.loading : null
        },
        loaded() {
            return this.restaurant && this.restaurant.id ? this.restaurant.loaded : null
        },
        cover() {
            return this.restaurant && this.restaurant.id ? this.restaurant.cover_image : null
        },
        categories() {
            return this.restaurant && this.restaurant.id ? this.restaurant.categories : null
        },
        sortedCategories() {
            return this.sortNull(this.categories)
        },
        pageKeywords() {
            return this.restaurant?.tags?.map((tag) => tag.name_token)?.join(', ')
        },
        // Canonical is always the clean restaurant URL: the optional menu-category segment,
        // the canteen prefix and the query only select a view of the same page.
        pageLink() {
            const slug = this.$route.params.slug
            if (!window.location || !slug) return undefined
            const { href } = this.$router.resolve({ name: 'restaurant', params: { slug } })
            return `${window.location.origin}${href}`
        },
    },

    watch: {
        '$route.query.location': {
            deep: true,
            handler(location, locationBefore) {
                if (location?.toString() !== locationBefore?.toString()) {
                    this.setRestaurant(this.$route.params, this.cart)
                }
            },
        },
    },

    mounted() {
        EventBus.$on('language', () => {
            this.setRestaurant(this.$route.params, this.cart)
            this.setMetaTitle()
        })
    },

    methods: {
        disconnectNavigationObserver() {
            this.$refs.categoryNav?.disconnect()
        },
        setMetaTitle() {
            this.pageTitle = this.meal.name || this.restaurant?.name
            this.pageImage = this.meal.image || this.restaurant?.card_image
            this.pageDescription = this.strip(this.meal.description) || this.restaurant?.description
            this.$meta().refresh()
        },
        strip(html) {
            if (!html) return
            let doc = new DOMParser().parseFromString(html, 'text/html')
            return doc.body.textContent || ''
        },
        async prepareCanteen(id, type) {
            if (id && type) {
                await store.dispatch('canteen/getResidentialList')
                let canteen
                id = parseInt(id)
                if (type === CLOUD && user.isLoggedIn) {
                    canteen = user?.canteens?.find((canteen) => canteen.id === id)
                } else if (type === RESIDENTIAL) {
                    canteen = store.getters['canteen/residentialList']?.find(
                        (canteen) => canteen.id === id
                    )
                }

                if (canteen) {
                    await store.dispatch('active/setCanteen', canteen)
                    cart.address = canteen.isResidential
                        ? canteen.address
                        : addresses.findItem(mixins.getId(canteen.address))
                } else
                    store.dispatch('toastInfo', {
                        title: Vue.i18n.translate('base.info'),
                        text: Vue.i18n.translate('alerts.canteen'),
                    })
            }
        },
        async fetchRestaurant(params) {
            return await restaurant.fetchRestaurant(
                params.slug,
                params.type && params.id ? { [`${params.type}canteen`]: params.id } : {}
            )
        },
        async setRestaurant(params, cart, beforeRouteEnter = false) {
            try {
                if (!beforeRouteEnter) await this.fetchRestaurant(params)
                await Promise.all([
                    store.dispatch('active/setRestaurant', restaurant.model),
                    store.dispatch('active/setCurrency', restaurant.country),
                ])
                if (!cart.restaurant) {
                    cart.resetCart()
                    cart.restaurant = restaurant.link
                    cart.calculate()
                } else if (restaurant) {
                    if (mixins.getId(cart.restaurant) === restaurant?.id) {
                        cart.calculate()
                    } else {
                        if (!cart.calculated) cart.calculate()
                        cart.calculate(restaurant.link)
                    }
                } else cart.calculate()

                user.getExpenseRules()
                return
            } catch (e) {
                throw new Error(e)
            } finally {
                this.pageLoading = false
            }
        },
    },
}
